import { create } from 'zustand';
import { RemoteDataSource, Settings } from '@/datasources/remote_datasource';
import { AppConfig } from "@/datasources/AppConfig";
import { resolveMediaUrl } from "@/lib/media";

export type AgenteStatus = 'Aprovado' | 'Reprovado' | 'AguardandoAprovacao' | string;

export interface CurrentAgenteData {
  id: string;
  nome: string;
  email?: string;
  telefone?: string;
  status?: AgenteStatus;
  arquivado: boolean;
  gestor?: boolean;
  fotoUrl?: string;
}

interface CurrentAgenteState {
  agente: CurrentAgenteData | null;
  status: AgenteStatus | null;
  isLoading: boolean;
  error: string | null;
  hasFetched: boolean;
  fetchCurrentAgente: (force?: boolean) => Promise<void>;
}

const api = new RemoteDataSource(Settings.urlBase);

export const useCurrentAgenteStore = create<CurrentAgenteState>((set, get) => ({
  agente: null,
  status: null,
  isLoading: false,
  error: null,
  hasFetched: false,

  fetchCurrentAgente: async (force = false) => {
    if (!AppConfig.agenteId || !AppConfig.ouvidoriaId) return;
    if (!force && get().hasFetched) return;

    set({ isLoading: true, error: null });
    try {
      // Usa o endpoint específico para obter o agente por ID
      const resAgente = await api.get<Record<string, unknown>>({
        requiredToken: true,
        headers: { accept: 'application/vnd.data.full.v1+json' },
        forceRefresh: true,
        endpoint: `v1/ouvidorias/${AppConfig.ouvidoriaId}/agentes/${AppConfig.agenteId}`,
      });

      if ('failure' in resAgente) {
        set({
          isLoading: false,
          error: resAgente.failure.messages?.join(', ') || 'Erro ao carregar agente atual',
          hasFetched: true,
        });
        return;
      }

      const raw = resAgente.success.data ?? resAgente.success.body as Record<string, unknown>;

      if (!raw) {
        set({
          agente: null,
          status: null,
          isLoading: false,
          hasFetched: true,
          error: 'Agente atual não encontrado na ouvidoria',
        });
        return;
      }

      // A API pode retornar um array, então pegamos o primeiro elemento
      const agenteRaw = Array.isArray(raw) ? raw[0] : raw;

      if (!agenteRaw) {
        set({
          agente: null,
          status: null,
          isLoading: false,
          hasFetched: true,
          error: 'Agente atual não encontrado na ouvidoria',
        });
        return;
      }

      // Determina o status do payload do agente
      let status: AgenteStatus | undefined;
      if (typeof agenteRaw.status === 'string' && agenteRaw.status) {
        status = agenteRaw.status as AgenteStatus;
      } else {
        // Se o status não vier no payload, assume Aprovado como padrão
        status = 'Aprovado';
      }

      const fotoPerfil = agenteRaw.fotoPerfil as Record<string, unknown> | undefined;
      const agente: CurrentAgenteData = {
        id: String(agenteRaw.id || ''),
        nome: String(agenteRaw.nome || ''),
        email: (agenteRaw.email as string) || undefined,
        telefone: (agenteRaw.telefone as string) || undefined,
        status,
        arquivado: Boolean(agenteRaw.arquivado),
        gestor: Boolean(agenteRaw.gestor),
        fotoUrl: resolveMediaUrl(fotoPerfil?.arquivo as string) || undefined,
      };

      set({
        agente,
        status: status ?? null,
        isLoading: false,
        hasFetched: true,
      });
    } catch (err) {
      set({
        isLoading: false,
        error: err instanceof Error ? err.message : 'Erro ao carregar agente atual',
        hasFetched: true,
      });
    }
  },
}));
